import { useCallback, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { analyticsService } from '@/services/analyticsService';
import { CornerIcon } from '@/components/CornerIcon';
import { OptimizedVideoAnimation } from '@/components/performance/OptimizedVideoAnimation';
import { getAnimationUrl, getSupabaseIconUrl, SUPABASE_ICONS } from '@/utils/supabaseIcons';
import { useAuth } from '@/contexts/AuthContext';
import { Footer } from '@/components/Footer';

const PURCHASE_URL = 'https://goodacademy.ai/purchase-course/lyra-course/';
const LOGIN_URL = '/auth?signin=true';

const Index = () => {
  const { user } = useAuth();

  // Track landing page view on mount
  useEffect(() => {
    if (!user) return;

    analyticsService.trackAnalyticsEvent('page_view', 'landing_page', {
      timestamp: new Date().toISOString()
    });
  }, [user]);

  const trackCtaClick = useCallback((eventName: string, eventData?: Record<string, any>) => {
    if (!user) return;

    analyticsService.trackAnalyticsEvent('cta_click', eventName, {
      ...(eventData || {}),
      userAuthenticated: !!user,
      timestamp: new Date().toISOString()
    });
  }, [user]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <CornerIcon />

      {/* Hero Section */}
      <main className="flex-grow flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl mx-auto text-center space-y-8">

          {/* Lyra Avatar / Animation */}
          <div className="flex justify-center mb-8">
            <div className="w-32 h-32 sm:w-40 sm:h-40 relative">
              <OptimizedVideoAnimation
                src={getAnimationUrl('lyra-landing-page.mp4')}
                fallbackIcon={
                  <img
                    src={getSupabaseIconUrl(SUPABASE_ICONS.heroMain)}
                    alt="Lyra AI Mentor"
                    className="w-full h-full object-contain"
                  />
                }
                className="w-full h-full"
                context="ui"
                autoPlay={true}
                loop={true}
                muted={true}
              />
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              For Non-Profit & Public Interest Professionals
            </p>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 tracking-tight">
              Build AI Confidence, <br />
              <span className="text-slate-500">One Tiny Win at a Time</span>
            </h1>
          </div>

          <div className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-600 leading-relaxed space-y-4">
            <p>
              Practical, 5-minute lessons designed for busy non-profit professionals. No hype, no jargon - just steady progress.
            </p>
            <p className="text-base text-slate-500">
              A license is required to use Lyra. If you are new to Lyra, please visit Good Academy to purchase access.
            </p>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={PURCHASE_URL}
              onClick={() => trackCtaClick('landing_purchase_access', { location: 'hero_primary' })}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-slate-900 hover:bg-slate-800 text-white text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
            >
              Purchase Access to Lyra
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={LOGIN_URL}
              onClick={() => trackCtaClick('landing_returning_user', { location: 'hero_secondary' })}
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-slate-300 text-slate-900 text-lg font-semibold hover:border-slate-500 transition"
            >
              Returning Users: Log In
            </a>
          </div>
        </div>

        <Footer />
      </main>
    </div>
  );
};

export default Index;
